import { useRef, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Paperclip, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Input, PartnerPicker } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker'
import { useHasAnyPermission } from '@/shell/module-system'
import { EDIT_ROLES } from '../utils/permissions'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useDeleteSampleLabel,
  useLinkOdooDelivery,
  usePatchSampleRequest,
  useSampleRequestDetail,
  useTransitionSampleRequest,
  useUploadSampleLabel,
} from '../api/queries'
import type { Stage } from '../api/types'
import {
  STAGE_LABELS,
  STAGE_VARIANT,
  allowedTargetsFrom,
  needByUrgencyForStage,
} from '../utils/stage-meta'
import { StageHistoryTimeline } from '../components/StageHistoryTimeline'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SampleRequestDetailPage() {
  const params = useParams({ strict: false }) as { requestId?: string }
  const requestId = params.requestId ?? ''
  const navigate = useNavigate()
  const detail = useSampleRequestDetail(requestId)
  const transition = useTransitionSampleRequest()
  const patch = usePatchSampleRequest()
  const linkOdoo = useLinkOdooDelivery()
  const uploadLabel = useUploadSampleLabel()
  const deleteLabel = useDeleteSampleLabel()

  const [ownerDraft, setOwnerDraft] = useState<string | null>(null)
  const [pickingRef, setPickingRef] = useState('')
  const labelInputRef = useRef<HTMLInputElement>(null)

  // Read-only roles (the external uviva vendor) see the detail but can't edit
  // fields, move stages, or link Odoo. See ../utils/permissions.ts.
  const canEdit = useHasAnyPermission(EDIT_ROLES)

  const req = detail.data

  if (detail.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-tertiary">Loading…</p>
      </div>
    )
  }

  if (!req) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-tertiary">Sample request not found.</p>
        <Button variant="secondary" size="sm" onClick={() => navigate({ to: '/sample-requests' })}>
          Back to board
        </Button>
      </div>
    )
  }

  const allowedTargets = allowedTargetsFrom(req.status)
  const customer = req.customer_name ?? req.prospect_name ?? '—'
  const ownerValue = ownerDraft ?? req.assigned_to ?? ''

  function handleTransition(target: Stage) {
    transition.mutate(
      { requestId: req!.id, targetStage: target },
      {
        onSuccess: () => toast.success(`Moved to ${STAGE_LABELS[target]}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'transition failed', {
            duration: 5000,
          }),
      },
    )
  }

  function saveOwner() {
    patch.mutate(
      { requestId: req!.id, edits: { assigned_to: ownerValue.trim() || null } },
      {
        onSuccess: () => {
          toast.success('Owner updated')
          setOwnerDraft(null)
        },
        onError: () => toast.error('Update failed'),
      },
    )
  }

  function saveContact(picked: PickerValue) {
    patch.mutate(
      {
        requestId: req!.id,
        edits: {
          contact_id: picked ? picked[0] : null,
          contact_name: picked ? picked[1] : null,
        },
      },
      {
        onSuccess: () => toast.success('Contact updated'),
        onError: () => toast.error('Update failed'),
      },
    )
  }

  function saveNeedBy(value: string) {
    patch.mutate(
      { requestId: req!.id, edits: { need_by_date: value || null } },
      {
        onSuccess: () => toast.success('Need-by updated'),
        onError: () => toast.error('Update failed'),
      },
    )
  }

  function submitLink() {
    if (!pickingRef.trim()) return
    linkOdoo.mutate(
      { requestId: req!.id, pickingReference: pickingRef.trim() },
      {
        onSuccess: () => {
          toast.success('Linked Odoo delivery')
          setPickingRef('')
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'link failed', { duration: 5000 }),
      },
    )
  }

  function handleLabelUpload(files: FileList | null) {
    if (!files || files.length === 0) return
    const chosen = Array.from(files)
    Promise.allSettled(
      chosen.map((file) => uploadLabel.mutateAsync({ requestId: req!.id, file })),
    ).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) toast.error(`${failed} label${failed > 1 ? 's' : ''} failed to upload`)
      else toast.success(`Attached ${chosen.length} label${chosen.length > 1 ? 's' : ''}`)
    })
    if (labelInputRef.current) labelInputRef.current.value = ''
  }

  function removeLabel(documentId: number, filename: string) {
    deleteLabel.mutate(
      { requestId: req!.id, documentId },
      {
        onSuccess: () => toast.success(`Removed ${filename}`),
        onError: () => toast.error('Remove failed'),
      },
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <header className="flex flex-shrink-0 items-center gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/sample-requests' })}
          aria-label="Back to board"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex flex-col">
          <h1 className="text-role-page-title m-0">{customer}</h1>
          <p className="text-role-page-subtitle m-0">{req.request_number}</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {req.overdue ? <Chip size="xs" variant="danger">Overdue to ship</Chip> : null}
          <Chip size="xs" variant={STAGE_VARIANT[req.status]} dot>
            {STAGE_LABELS[req.status]}
          </Chip>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => detail.refetch()}
            disabled={detail.isFetching}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 gap-0">
        <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 py-6">
          {/* Details */}
          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <h2 className="text-role-section-label mb-4">Request</h2>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Field label="Customer / prospect" value={customer} primary />
              {req.customer_id == null && req.prospect_company ? (
                <Field label="Prospect company" value={req.prospect_company} />
              ) : null}
              {req.customer_id == null && req.prospect_email ? (
                <Field label="Prospect email" value={req.prospect_email} />
              ) : null}
              {req.customer_id == null && req.prospect_phone ? (
                <Field label="Prospect phone" value={req.prospect_phone} />
              ) : null}
              {canEdit ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Contact</dt>
                  <dd className="m-0">
                    <PartnerPicker
                      value={
                        req.contact_id != null && req.contact_name
                          ? [req.contact_id, req.contact_name]
                          : null
                      }
                      onChange={saveContact}
                      kind="customer_contact"
                      parentId={req.customer_id}
                      placeholder="Pick a contact at this company…"
                      disabled={req.customer_id == null}
                    />
                  </dd>
                </div>
              ) : (
                <Field label="Contact" value={req.contact_name ?? '—'} />
              )}
              {canEdit ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Owner</dt>
                  <dd className="m-0 flex items-center gap-2">
                    <Input
                      value={ownerValue}
                      onChange={(e) => setOwnerDraft(e.target.value)}
                      placeholder="owner@siliconesplus.com"
                    />
                    {ownerDraft !== null && ownerDraft !== (req.assigned_to ?? '') ? (
                      <Button variant="secondary" size="sm" onClick={saveOwner} loading={patch.isPending}>
                        Save
                      </Button>
                    ) : null}
                  </dd>
                </div>
              ) : (
                <Field label="Owner" value={req.assigned_to ?? '—'} />
              )}
              <Field label="Requested by" value={req.requested_by ?? '—'} />
              <div>
                <dt className="text-role-metadata mb-0.5 flex items-center gap-2">
                  Need by
                  {(() => {
                    const urgency = needByUrgencyForStage(req.need_by_date, req.status)
                    return urgency ? (
                      <Chip size="xs" variant={urgency.variant}>
                        {urgency.label}
                      </Chip>
                    ) : null
                  })()}
                </dt>
                <dd className="m-0">
                  {canEdit ? (
                    <Input
                      type="date"
                      value={req.need_by_date ?? ''}
                      onChange={(e) => saveNeedBy(e.target.value)}
                      aria-label="Need by"
                    />
                  ) : (
                    <span className="text-role-data-secondary">
                      {formatDate(req.need_by_date)}
                    </span>
                  )}
                </dd>
              </div>
              <Field
                label="SLA due"
                value={req.sla_due_at ? formatDate(req.sla_due_at) : '—'}
              />
              <div>
                <dt className="text-role-metadata mb-0.5">Sales order</dt>
                <dd className="m-0 text-role-data-secondary">
                  {req.odoo_so_id && req.odoo_so_name ? (
                    <a
                      href={odooUrl('sale.order', req.odoo_so_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-link hover:underline"
                    >
                      {req.odoo_so_name}
                    </a>
                  ) : (
                    <span className="text-tertiary">— not yet created —</span>
                  )}
                </dd>
              </div>
              <Field
                label="Opportunity"
                value={req.crm_lead_name ?? '—'}
              />
              <Field label="Requested" value={formatDate(req.created_at)} />
            </dl>
            {req.ship_to_address ? (
              <div className="mt-4">
                <div className="text-role-metadata mb-0.5">Ship-to</div>
                <div className="whitespace-pre-line text-sm text-secondary">{req.ship_to_address}</div>
              </div>
            ) : null}
            {req.notes ? (
              <div className="mt-4 rounded bg-surface-sunk p-3 text-sm text-secondary">{req.notes}</div>
            ) : null}
          </section>

          {/* Items */}
          {req.lines && req.lines.length > 0 ? (
            <section className="rounded-lg border border-border-subtle bg-surface p-5">
              <h2 className="text-role-section-label mb-4">Requested products</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-role-table-header py-2 text-left">Code</th>
                    <th className="text-role-table-header py-2 text-left">Description</th>
                    <th className="text-role-table-header py-2 text-right">Qty</th>
                    <th className="text-role-table-header py-2 text-left">UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {req.lines.map((line) => (
                    <tr key={line.id} className="border-b border-border-subtle last:border-0">
                      <td className="text-role-table-cell py-2">{line.product_code ?? '—'}</td>
                      <td className="text-role-table-cell py-2">{line.description ?? '—'}</td>
                      <td className="text-role-table-cell py-2 text-right">
                        {line.quantity != null ? line.quantity : '—'}
                      </td>
                      <td className="text-role-table-cell py-2">{line.uom ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {/* Shipping labels */}
          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-role-section-label">Shipping labels</h2>
              {canEdit ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={uploadLabel.isPending}
                    onClick={() => labelInputRef.current?.click()}
                  >
                    <Paperclip size={14} className="mr-1.5" /> Attach label
                  </Button>
                  <input
                    ref={labelInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => handleLabelUpload(e.target.files)}
                  />
                </>
              ) : null}
            </div>
            {req.labels.length === 0 ? (
              <p className="text-role-metadata">No shipping labels attached.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {req.labels.map((label) => (
                  <li
                    key={label.document_id}
                    className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-sunk px-3 py-1.5 text-sm"
                  >
                    <Paperclip size={14} className="text-secondary" />
                    <a
                      href={`/api/documents/${label.document_id}/${encodeURIComponent(label.filename)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate text-link hover:underline"
                    >
                      {label.filename}
                    </a>
                    {canEdit ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Remove ${label.filename}`}
                        onClick={() => removeLabel(label.document_id, label.filename)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Delivery + tracking */}
          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <h2 className="text-role-section-label mb-4">Delivery &amp; tracking</h2>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Field
                label="Odoo delivery"
                value={req.odoo_picking_name ?? '— not linked —'}
                primary={!!req.odoo_picking_name}
              />
              <Field label="Delivery state" value={req.odoo_picking_state ?? '—'} />
              <Field
                label="Tracking #"
                value={
                  req.tracking_number ? (
                    req.tracking_link ? (
                      <a
                        href={req.tracking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-link hover:underline"
                      >
                        {req.tracking_number}
                      </a>
                    ) : (
                      req.tracking_number
                    )
                  ) : (
                    '—'
                  )
                }
              />
              <Field label="Carrier" value={req.carrier_name ?? '—'} />
              <Field label="Carrier status" value={req.tracking_status ?? '—'} />
              <Field label="Lot #" value={req.lot_number ?? '—'} />
              <Field label="Expiration" value={formatDate(req.expiration_date)} />
              <Field label="Shipped" value={formatDate(req.shipped_at)} />
              <Field label="Delivered" value={formatDate(req.delivered_at)} />
            </dl>
            {!req.odoo_picking_name ? (
              canEdit ? (
                <div className="mt-4 flex items-end gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-role-metadata">Link Odoo delivery by reference</span>
                    <Input
                      value={pickingRef}
                      onChange={(e) => setPickingRef(e.target.value)}
                      placeholder="e.g. WH/OUT/01234"
                    />
                  </label>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={submitLink}
                    loading={linkOdoo.isPending}
                    disabled={!pickingRef.trim()}
                  >
                    Link
                  </Button>
                </div>
              ) : null
            ) : (
              <p className="text-role-metadata mt-3">
                The reconciler keeps state + tracking in sync with Odoo automatically.
              </p>
            )}
          </section>
        </div>

        {/* Right: stage controls + history */}
        <aside className="w-72 flex-shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-5">
          {canEdit && allowedTargets.length > 0 ? (
            <section className="mb-6">
              <h3 className="text-role-section-label mb-3">Move pipeline</h3>
              <div className="flex flex-col gap-1.5">
                {allowedTargets.map((target) => (
                  <Button
                    key={target}
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    onClick={() => handleTransition(target)}
                    disabled={transition.isPending}
                  >
                    <Chip size="xs" variant={STAGE_VARIANT[target]} dot className="mr-2" />
                    {STAGE_LABELS[target]}
                  </Button>
                ))}
              </div>
            </section>
          ) : null}

          <section>
            <h3 className="text-role-section-label mb-3">History</h3>
            <StageHistoryTimeline entries={req.stage_history ?? []} />
          </section>
        </aside>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  primary,
}: {
  label: string
  value: React.ReactNode
  primary?: boolean
}) {
  return (
    <div>
      <dt className="text-role-metadata mb-0.5">{label}</dt>
      <dd className={`m-0 ${primary ? 'text-role-data-primary' : 'text-role-data-secondary'}`}>
        {value}
      </dd>
    </div>
  )
}
